import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AttendanceService } from "../service/attendanceService";
import { useReCaptcha } from "../context/ReCaptchaContext";
import { useNotification, NotificationSeverity } from "../context/NotificationContext";

export const useAttendance = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { executeVerification } = useReCaptcha();
  const { showNotification } = useNotification();

  // Estado principal del hook
  const [state, setState] = useState({
    qrToken: null,
    isValidToken: false,
    isLoading: true,
    tokenError: null,
    
    // Datos del usuario
    userData: null,
    isRegistered: false,
    lastRecord: null,
    hasShownInfoToday: false,
    previousVisitorData: null,
    
    // Control de flujo
    currentStep: 'entry', // 'entry', 'reason', 'info', 'feedback', 'terms', 'success'
    recordType: null, // 'entrada' o 'salida'
    hasSearched: false, // Nueva bandera para controlar si ya se realizó la búsqueda
    
    // Datos del formulario actualizado
    formData: {
      documentNumber: '',
      // Datos personales
      name: '',
      visitorType: '',
      phone: '',
      eps: '',
      arl: '',
      jobPosition: '',
      emergencyPhone: '',
      // Datos de visita
      visitArea: '',
      visitReason: '',
      visitReasonOther: '',
      expectedExitTime: '',
      company: ''
    },
    
    // Feedback de salida
    exitFeedback: {
      purposeFulfilled: null,
      areaMetExpectations: null,
      additionalComments: '',
      anonymousProcessing: false
    },
    
    // Términos
    termsAccepted: false
  });

  // Opciones de motivo de visita
  const visitReasons = [
    { value: 'trabajo', label: 'Trabajo' },
    { value: 'entrega', label: 'Entrega de documentos' },
    { value: 'mantenimiento', label: 'Mantenimiento técnico' },
    { value: 'evento', label: 'Participación en evento' },
    { value: 'reclamos', label: 'Reclamos o trámites administrativos' },
    { value: 'consulta', label: 'Consulta' },
    { value: 'capacitacion', label: 'Capacitación' },
    { value: 'otro', label: 'Otro:' }
  ];

  // Validar token QR al cargar
  useEffect(() => {
    const validateToken = async () => {
      const token = searchParams.get('tk');
      
      if (!token) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          tokenError: 'No se encontró código QR válido'
        }));
        return;
      }

      try {
        const validation = await AttendanceService.validateQRToken(token);
        
        if (validation.success) {
          setState(prev => ({
            ...prev,
            qrToken: token,
            isValidToken: true,
            isLoading: false
          }));
        } else {
          setState(prev => ({
            ...prev,
            isLoading: false,
            tokenError: validation.message,
            isExpired: validation.isExpired,
            isFuture: validation.isFuture
          }));
        }
      } catch (error) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          tokenError: 'Error al validar el código QR'
        }));
      }
    };

    validateToken();
  }, [searchParams]);

  /**
   * Busca un usuario por número de documento
   */
  const searchUser = useCallback(async (documentNumber) => {
    if (!state.qrToken) return;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await AttendanceService.searchUserByDocument(
        documentNumber, 
        state.qrToken
      );

      if (response.success) {
        const { userData, lastRecord, hasShownInfoToday, previousVisitorData } = response;
        
        // Determinar tipo de registro
        let recordType = 'entrada';
        if (lastRecord && lastRecord.type === 'entrada' && !lastRecord.hasExitRecord) {
          recordType = 'salida';
        }

        setState(prev => ({
          ...prev,
          isLoading: false,
          userData: userData || {},
          isRegistered: response.registered,
          lastRecord: lastRecord,
          hasShownInfoToday: hasShownInfoToday,
          previousVisitorData: previousVisitorData,
          recordType: recordType,
          hasSearched: true, // Marcar que ya se realizó la búsqueda
          formData: {
            ...prev.formData,
            documentNumber: documentNumber,
            // Pre-llenar campos si existe información
            ...(userData && {
              name: `${userData.firstName} ${userData.lastName}`,
              eps: userData.eps || prev.formData.eps || '',
              arl: userData.arl || prev.formData.arl || '',
              jobPosition: userData.jobPosition || prev.formData.jobPosition || '',
              emergencyPhone: userData.emergencyPhone || prev.formData.emergencyPhone || ''
            }),
            // Si no está registrado pero ya visitó antes, cargar datos previos
            ...(previousVisitorData && !response.registered && {
              name: previousVisitorData.name || prev.formData.name,
              phone: previousVisitorData.phone || prev.formData.phone,
              eps: previousVisitorData.eps || prev.formData.eps,
              arl: previousVisitorData.arl || prev.formData.arl,
              jobPosition: previousVisitorData.jobPosition || prev.formData.jobPosition,
              emergencyPhone: previousVisitorData.emergencyPhone || prev.formData.emergencyPhone,
              company: previousVisitorData.company || prev.formData.company,
              visitorType: previousVisitorData.visitorType || prev.formData.visitorType,
              visitArea: previousVisitorData.visitArea || prev.formData.visitArea
            })
          }
        }));
      }
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      showNotification({
        message: error.message || "Error al buscar usuario",
        severity: NotificationSeverity.ERROR
      });
    }
  }, [state.qrToken, showNotification]);

  /**
   * Actualiza los datos del formulario
   */
  const updateFormData = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        ...updates
      }
    }));
  }, []);

  /**
   * Actualiza el feedback de salida
   */
  const updateExitFeedback = useCallback((updates) => {
    setState(prev => ({
      ...prev,
      exitFeedback: {
        ...prev.exitFeedback,
        ...updates
      }
    }));
  }, []);

  /**
   * Determina si debe mostrar información HSEQ
   */
  const shouldShowInfo = useCallback(() => {
    // Para entradas
    if (state.recordType === 'entrada') {
      // Personal de oficina: mostrar solo una vez al día
      if (state.isStaff && !state.hasShownInfoToday) {
        return true;
      }
      // Otros usuarios registrados (operadores, etc): siempre mostrar
      if (state.isRegistered && !state.isStaff) {
        return true;
      }
      // Visitantes no registrados: siempre mostrar
      if (!state.isRegistered) {
        return true;
      }
    }
    return false;
  }, [state]);

  /**
   * Determina si debe mostrar feedback de salida
   */
  const shouldShowExitFeedback = useCallback(() => {
    if (state.recordType !== 'salida') return false;
    
    // No mostrar para personal de oficina
    if (state.isStaff) return false;
    
    // Mostrar para todos los demás
    return true;
  }, [state]);

  /**
   * Avanza al siguiente paso
   */
  const nextStep = useCallback(() => {
    const stepFlow = ['entry'];
    
    // Agregar paso de motivo si aplica
    if (state.recordType === 'entrada' && !state.isStaff && 
        state.formData.visitorType !== 'personal') {
      stepFlow.push('reason');
    }
    
    // Agregar información si aplica
    if (shouldShowInfo()) {
      stepFlow.push('info');
    }
    
    // Agregar feedback si aplica
    if (shouldShowExitFeedback()) {
      stepFlow.push('feedback');
    }
    
    // Siempre agregar términos
    stepFlow.push('terms');
    
    const currentIndex = stepFlow.indexOf(state.currentStep);
    if (currentIndex < stepFlow.length - 1) {
      setState(prev => ({ ...prev, currentStep: stepFlow[currentIndex + 1] }));
    }
  }, [state, shouldShowInfo, shouldShowExitFeedback]);

  /**
   * Retrocede al paso anterior
   */
  const previousStep = useCallback(() => {
    const stepFlow = ['entry'];
    
    if (state.recordType === 'entrada' && !state.isStaff && 
        state.formData.visitorType !== 'personal') {
      stepFlow.push('reason');
    }
    
    if (shouldShowInfo()) {
      stepFlow.push('info');
    }
    
    if (shouldShowExitFeedback()) {
      stepFlow.push('feedback');
    }
    
    stepFlow.push('terms');
    
    const currentIndex = stepFlow.indexOf(state.currentStep);
    if (currentIndex > 0) {
      setState(prev => ({ ...prev, currentStep: stepFlow[currentIndex - 1] }));
    }
  }, [state, shouldShowInfo, shouldShowExitFeedback]);

  /**
   * Envía el formulario de asistencia
   */
  const submitAttendance = useCallback(async () => {
    if (!state.qrToken || !state.termsAccepted) return;

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      // Ejecutar reCAPTCHA
      const captchaToken = process.env.REACT_APP_ENV === 'production' 
        ? await executeVerification('attendance_register')
        : 'dummy_token_for_development';

      if (!captchaToken && process.env.REACT_APP_ENV === 'production') {
        throw new Error("Error en la verificación de seguridad");
      }

      // Preparar datos
      const attendanceData = {
        documentNumber: state.formData.documentNumber,
        recordType: state.recordType,
        formData: state.formData,
        isRegistered: state.isRegistered
      };

      // Agregar feedback si aplica
      if (state.recordType === 'salida' && shouldShowExitFeedback()) {
        attendanceData.exitFeedback = state.exitFeedback;
      }

      // Registrar asistencia
      const response = await AttendanceService.registerAttendance(
        attendanceData,
        state.qrToken,
        captchaToken
      );

      if (response.success) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          currentStep: 'success'
        }));

        showNotification({
          message: response.message,
          severity: NotificationSeverity.SUCCESS
        });

        // Redirigir después de 5 segundos
        setTimeout(() => {
          navigate('/');
        }, 5000);
      }
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      showNotification({
        message: error.message || "Error al registrar asistencia",
        severity: NotificationSeverity.ERROR
      });
    }
  }, [state, executeVerification, navigate, showNotification, shouldShowExitFeedback]);

  /**
   * Actualiza aceptación de términos
   */
  const setTermsAccepted = useCallback((accepted) => {
    setState(prev => ({ ...prev, termsAccepted: accepted }));
  }, []);

  return {
    // Estado
    ...state,
    
    // Funciones
    searchUser,
    updateFormData,
    updateExitFeedback,
    shouldShowExitFeedback,
    shouldShowInfo,
    nextStep,
    previousStep,
    submitAttendance,
    setTermsAccepted,
    
    // Utilidades
    isStaff: state.userData?.isStaff || false,
    canProceed: state.isValidToken && !state.tokenError,
    visitReasons,
    hasSearched: state.hasSearched,
    previousVisitorData: state.previousVisitorData
  };
};